import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientEvents, RoomView } from '@shared/types';
import { call, getSocket } from './socket';
import { forgetSeat, getAvatar, getSeat, saveName, saveSeat } from './storage';

export type RoomStatus = 'connecting' | 'needs-name' | 'in' | 'gone';

/**
 * Keeps this tab seated in a room: resumes with the saved token on load and on every reconnect,
 * and exposes the latest personalised room state.
 */
export function useRoom(code: string) {
  const [status, setStatus] = useState<RoomStatus>('connecting');
  const [view, setView] = useState<RoomView | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [online, setOnline] = useState(true);
  const clockOffset = useRef(0);

  const resume = useCallback(async () => {
    const seat = getSeat(code);
    if (!seat) {
      setStatus((s) => (s === 'in' ? s : 'needs-name'));
      return;
    }
    const res = await call('room:resume', { code, token: seat.token });
    if (res.ok) {
      setStatus('in');
      setMessage(null);
    } else if (/No room/.test(res.error)) {
      forgetSeat(code);
      setMessage(res.error);
      setStatus('gone');
    } else {
      forgetSeat(code);
      setMessage(res.error);
      setStatus('needs-name');
    }
  }, [code]);

  useEffect(() => {
    const socket = getSocket();
    const onState = (v: RoomView) => {
      if (v.code !== code) return;
      clockOffset.current = v.serverTime - Date.now();
      setView(v);
    };
    const onRemoved = ({ reason }: { reason: 'kicked' | 'closed' | 'left' }) => {
      forgetSeat(code);
      setView(null);
      setMessage(
        reason === 'kicked'
          ? 'The host removed you from this room.'
          : reason === 'left'
            ? 'You opened this room in another tab, so this one stepped aside.'
            : 'This room has closed.',
      );
      setStatus('gone');
    };
    const onConnect = () => {
      setOnline(true);
      void resume();
    };
    const onDisconnect = () => setOnline(false);
    socket.on('room:state', onState);
    socket.on('room:removed', onRemoved);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (socket.connected) void resume();
    return () => {
      socket.off('room:state', onState);
      socket.off('room:removed', onRemoved);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [code, resume]);

  const join = useCallback(
    async (name: string) => {
      const res = await call('room:join', { code, name, avatar: getAvatar() || undefined });
      if (!res.ok) return res.error;
      saveName(name);
      saveSeat(res.code, { token: res.token, playerId: res.playerId });
      setStatus('in');
      setMessage(null);
      return null;
    },
    [code],
  );

  /** Send a game action; resolves to an error message or null. */
  const act = useCallback(async <E extends keyof ClientEvents>(event: E, payload: Parameters<ClientEvents[E]>[0]) => {
    const res = (await call(event, payload)) as { ok: boolean; error?: string };
    return res.ok ? null : (res.error ?? 'Something went wrong.');
  }, []);

  const leave = useCallback(async () => {
    await call('room:leave', {});
    forgetSeat(code);
  }, [code]);

  const serverNow = useCallback(() => Date.now() + clockOffset.current, []);

  return { status, view, message, online, join, act, leave, serverNow };
}

export type RoomApi = ReturnType<typeof useRoom>;
